<template>
    <div>
        <div
            v-if="chartYears.length > 0"
            class="uk-margin-small-bottom">
            <div class="title dark">
                <Translate translation-key="processed.pictures.per.year" />
            </div>
            <div ref="yearChart" class="chart-container" />
        </div>

        <div class="title dark"><Translate translation-key="licenses" /></div>
        <div class="uk-margin-bottom" />

        <div
            class="uk-grid-small uk-grid-match uk-child-width-1-3@l uk-child-width-1-2@s uk-child-width-1-1 uk-flex uk-flex-center"
            uk-grid>
            <div>
                <License
                    :licensed-until="settings.licensedUntil"
                    :description="$t('photoUpload')" />
            </div>
            <div>
                <License
                    :licensed-until="settings.galleryLicensedUntil"
                    :description="$t('gallery')" />
            </div>
            <div>
                <License
                    :licensed-until="settings.terminalLicensedUntil"
                    :description="$t('terminal')" />
            </div>
            <div>
                <License
                    :licensed-until="settings.transferLicensedUntil"
                    :description="$t('transfer')" />
            </div>
            <div>
                <License
                    :licensed-until="settings.clickAndBuyLicensedUntil"
                    :description="$t('clickandbuy')" />
            </div>
        </div>

        <div class="uk-margin-medium-top">
            <a
                :href="generateLicenseUpgrade()"
                target="_blank"
                v-on:click="openLicenseUpgrade()">
                <Button isLarge isMax mode="success">
                    <Icon icon="star" /> {{ $t('license.upgrade') }}
                </Button>
            </a>
        </div>
    </div>
</template>

<script>
import Button from '@/components/generic/Button.vue';
import Icon from '@/components/generic/Icon.vue';
import License from '@/components/License.vue';
import Translate from '@/components/Translate.vue';
import Highcharts from 'highcharts';
import UIkit from 'uikit';

export default {
    name: 'AdminLicenses',
    components: {Translate, License, Icon, Button},
    props: {
        settings: Object,
        isClickAndBuyOnly: Boolean,
    },
    data() {
        return {
            title: 'licenses',
            yearChartInstance: null,
        };
    },
    watch: {
        chartYears: {
            handler() {
                this.$nextTick(() => this.renderYearChart());
            },
            immediate: true,
        },
    },
    mounted() {
        // the tab is hidden when the chart is drawn; fit it once it shows
        UIkit.util.on(document, 'shown', this.reflowYearChart);
    },
    beforeDestroy() {
        UIkit.util.off(document, 'shown', this.reflowYearChart);
        this.yearChartInstance?.destroy();
    },
    computed: {
        // years with LunaCart orders only have sales but no pictures
        chartYears() {
            return (this.settings.orderStatsYears || []).filter(
                (year) => year.pictures > 0
            );
        },
        computedLicenseLink() {
            if (this.isClickAndBuyOnly) {
                return 'https://clickandbuy.app/license_upgrade.php?domain=';
            }

            return 'https://www.phy-prints.com/phy_configurator.php?domain=';
        },
    },
    methods: {
        // a column per year, stacked by license, the total on top
        renderYearChart() {
            this.yearChartInstance?.destroy();
            this.yearChartInstance = null;

            const years = this.chartYears;
            if (years.length === 0 || !this.$refs.yearChart) {
                return;
            }

            const names = {
                upload: 'photoUpload',
                terminal: 'terminal',
                gallery: 'gallery',
                // stats written before the source field (Jan 2024)
                '': 'other',
            };
            const sources = [];
            years.forEach((year) => {
                Object.keys(year.sources).forEach((source) => {
                    if (year.sources[source] > 0 && !sources.includes(source)) {
                        sources.push(source);
                    }
                });
            });

            // 'tw' is Twi as a locale tag, as in App.vue's html lang
            const locale =
                this.$i18n.locale === 'tw' ? 'zh-TW' : this.$i18n.locale;
            const fmt = (v) => v.toLocaleString(locale);

            this.yearChartInstance = Highcharts.chart(this.$refs.yearChart, {
                chart: {type: 'column'},
                title: {text: null},
                xAxis: {categories: years.map((year) => String(year.year))},
                yAxis: {
                    min: 0,
                    allowDecimals: false,
                    title: {text: null},
                    stackLabels: {
                        enabled: true,
                        formatter() {
                            return fmt(this.total);
                        },
                    },
                },
                legend: {enabled: true},
                tooltip: {
                    shared: true,
                    formatter() {
                        return this.points.reduce(
                            (text, p) =>
                                text +
                                '<span style="color:' +
                                p.color +
                                '">●</span> ' +
                                p.series.name +
                                ': <span style="font-weight:500">' +
                                fmt(p.y) +
                                '</span><br/>',
                            // this.x is the column index, as in LunaStatsAdmin
                            '<b>' +
                                this.points[0].series.chart.xAxis[0].categories[
                                    this.x
                                ] +
                                '</b><br/>'
                        );
                    },
                },
                plotOptions: {
                    column: {
                        stacking: 'normal',
                        borderWidth: 0,
                    },
                },
                series: sources.map((source) => ({
                    name: this.$t(names[source] || source),
                    // null, not 0: the shared tooltip skips null points
                    data: years.map((year) => year.sources[source] || null),
                })),
                credits: {enabled: false},
            });
        },
        // 'shown' also comes from every tooltip; only the pane holding the
        // chart matters
        reflowYearChart(event) {
            if (
                this.yearChartInstance &&
                event.target.contains(this.$el) &&
                this.$el.offsetParent
            ) {
                this.yearChartInstance.reflow();
            }
        },
        generateLicenseUpgrade() {
            return this.computedLicenseLink + window.location.origin;
        },

        openLicenseUpgrade() {
            event.stopPropagation();
            event.preventDefault();
            window.open(this.generateLicenseUpgrade(), '_blank').focus();
        },
    },
};
</script>

<style lang="scss" scoped>
.title {
    margin-bottom: 30px;
    text-align: left;
    color: $site-color-hover;
    font-weight: 500;
    font-size: 18px;

    &.dark {
        color: $site-background-hover;
    }
}

.chart-container {
    width: 100%;
    min-height: 300px;
}
</style>
